import React, { useRef, useEffect } from 'react';
import { setupCanvas } from './canvasUtils';

// ============================================
// Chapter 10  -  Experimental Safety
// ============================================

/** 10.2 – Radiation Protection */
export const RadiationProtection = () => {
    const c = useRef(null);
    useEffect(() => {
        const W = 560, H = 320;
        const ctx = setupCanvas(c.current, W, H);
        ctx.fillStyle = '#FAFAFA'; ctx.fillRect(0, 0, W, H);

        ctx.fillStyle = '#1E293B'; ctx.font = 'bold 15px Inter,sans-serif'; ctx.textAlign = 'center';
        ctx.fillText('Radiation Protection  -  ALARA Principle', W / 2, 24);

        // Three pillars
        const pillars = [
            { title: 'Time', icon: '⏱', desc: 'Minimise time\nnear sources', value: 'Dose ∝ Time', color: '#3B82F6' },
            { title: 'Distance', icon: '📏', desc: 'Maximise distance\nfrom sources', value: 'Dose ∝ 1/r²', color: '#22C55E' },
            { title: 'Shielding', icon: '🛡', desc: 'Use appropriate\nshielding material', value: 'Dose ∝ e^(-μx)', color: '#F59E0B' },
        ];

        pillars.forEach((p, i) => {
            const x = 30 + i * 180, y = 48, w = 158, h = 145;
            ctx.fillStyle = p.color + '10'; ctx.beginPath(); ctx.roundRect(x, y, w, h, 12); ctx.fill();
            ctx.strokeStyle = p.color; ctx.lineWidth = 2; ctx.beginPath(); ctx.roundRect(x, y, w, h, 12); ctx.stroke();

            ctx.fillStyle = p.color; ctx.font = '28px Inter,sans-serif'; ctx.textAlign = 'center';
            ctx.fillText(p.icon, x + w / 2, y + 34);
            ctx.font = 'bold 14px Inter,sans-serif';
            ctx.fillText(p.title, x + w / 2, y + 56);

            ctx.fillStyle = '#475569'; ctx.font = '11px Inter,sans-serif';
            p.desc.split('\n').forEach((line, li) => {
                ctx.fillText(line, x + w / 2, y + 76 + li * 14);
            });

            ctx.fillStyle = '#1E293B'; ctx.font = 'bold 12px Inter,sans-serif';
            ctx.fillText(p.value, x + w / 2, y + 122);
        });

        // ALARA box
        ctx.fillStyle = '#FEF2F2'; ctx.beginPath(); ctx.roundRect(80, 205, 400, 40, 8); ctx.fill();
        ctx.strokeStyle = '#FECACA'; ctx.lineWidth = 1.5; ctx.beginPath(); ctx.roundRect(80, 205, 400, 40, 8); ctx.stroke();
        ctx.fillStyle = '#DC2626'; ctx.font = 'bold 14px Inter,sans-serif'; ctx.textAlign = 'center';
        ctx.fillText('ALARA = As Low As Reasonably Achievable', W / 2, 230);

        ctx.fillStyle = '#64748B'; ctx.font = '11px Inter,sans-serif';
        ctx.fillText('All CERN personnel must follow radiation safety training before accessing controlled areas', W / 2, H - 10);
    }, []);
    return (
        <div className="my-8 p-6 bg-gradient-to-br from-blue-50 to-amber-50 rounded-2xl border border-slate-200">
            <canvas ref={c} width={560} height={320} className="mx-auto block rounded-xl bg-white shadow-inner" style={{ maxWidth: '100%' }} />
        </div>
    );
};

/** 10.3 – Electrical Safety */
export const ElectricalSafetyDiagram = () => {
    const c = useRef(null);
    useEffect(() => {
        const W = 540, H = 260;
        const ctx = setupCanvas(c.current, W, H);
        ctx.fillStyle = '#FAFAFA'; ctx.fillRect(0, 0, W, H);

        ctx.fillStyle = '#1E293B'; ctx.font = 'bold 15px Inter,sans-serif'; ctx.textAlign = 'center';
        ctx.fillText('Electrical Safety at Particle Accelerators', W / 2, 24);

        const hazards = [
            { title: 'High Voltage', desc: 'Magnet power supplies\nup to 20 kA, 10 kV', icon: '⚡', color: '#EF4444' },
            { title: 'Stored Energy', desc: 'LHC magnets store\n11 GJ total', icon: '🔋', color: '#F59E0B' },
            { title: 'RF Systems', desc: 'Radio-frequency cavities\nhigh EM fields', icon: '📡', color: '#3B82F6' },
        ];

        hazards.forEach((h, i) => {
            const x = 20 + i * 175, y = 45, w = 160, h2 = 110;
            ctx.fillStyle = h.color + '10'; ctx.beginPath(); ctx.roundRect(x, y, w, h2, 10); ctx.fill();
            ctx.strokeStyle = h.color; ctx.lineWidth = 2; ctx.beginPath(); ctx.roundRect(x, y, w, h2, 10); ctx.stroke();

            ctx.fillStyle = h.color; ctx.font = '22px Inter,sans-serif'; ctx.textAlign = 'center';
            ctx.fillText(h.icon, x + w / 2, y + 28);
            ctx.font = 'bold 13px Inter,sans-serif';
            ctx.fillText(h.title, x + w / 2, y + 48);

            ctx.fillStyle = '#475569'; ctx.font = '11px Inter,sans-serif';
            h.desc.split('\n').forEach((line, li) => {
                ctx.fillText(line, x + w / 2, y + 68 + li * 14);
            });
        });

        // Safety measures
        const sy = 170;
        ctx.fillStyle = '#F0FDF4'; ctx.beginPath(); ctx.roundRect(30, sy, 480, 55, 8); ctx.fill();
        ctx.strokeStyle = '#86EFAC'; ctx.lineWidth = 1; ctx.beginPath(); ctx.roundRect(30, sy, 480, 55, 8); ctx.stroke();
        ctx.fillStyle = '#16A34A'; ctx.font = 'bold 12px Inter,sans-serif'; ctx.textAlign = 'center';
        ctx.fillText('Safety measures:', W / 2, sy + 18);
        ctx.fillStyle = '#475569'; ctx.font = '12px Inter,sans-serif';
        ctx.fillText('Lock Out / Tag Out (LOTO)  •  Earth grounding  •  Insulation barriers  •  Access controls', W / 2, sy + 38);

        ctx.fillStyle = '#64748B'; ctx.font = '11px Inter,sans-serif';
        ctx.fillText('Electrical hazards are the leading cause of potential injury at accelerator facilities', W / 2, H - 10);
    }, []);
    return (
        <div className="my-8 p-6 bg-gradient-to-br from-red-50 to-amber-50 rounded-2xl border border-slate-200">
            <canvas ref={c} width={540} height={260} className="mx-auto block rounded-xl bg-white shadow-inner" style={{ maxWidth: '100%' }} />
        </div>
    );
};

/** 10.4 – Cryogenic Hazards */
export const CryogenicHazards = () => {
    const c = useRef(null);
    useEffect(() => {
        const W = 560, H = 320;
        const ctx = setupCanvas(c.current, W, H);
        ctx.fillStyle = '#FAFAFA'; ctx.fillRect(0, 0, W, H);

        ctx.fillStyle = '#1E293B'; ctx.font = 'bold 15px Inter,sans-serif'; ctx.textAlign = 'center';
        ctx.fillText('Cryogenic Hazards  -  Extreme Cold at the LHC', W / 2, 24);

        // Temperature scale
        const scaleX = 40, scaleW = 480, scaleY = 70;
        const temps = [
            { t: -271, label: 'LHC magnets\n1.9 K', color: '#3B82F6', x: 0.0 },
            { t: -269, label: 'Liquid He\n4.2 K', color: '#60A5FA', x: 0.1 },
            { t: -196, label: 'Liquid N₂\n77 K', color: '#22D3EE', x: 0.35 },
            { t: 0, label: 'Water freezes\n273 K', color: '#F59E0B', x: 0.72 },
            { t: 20, label: 'Room temp\n293 K', color: '#EF4444', x: 0.82 },
        ];

        // Gradient bar
        const grad = ctx.createLinearGradient(scaleX, 0, scaleX + scaleW, 0);
        grad.addColorStop(0, '#1E40AF'); grad.addColorStop(0.3, '#60A5FA'); grad.addColorStop(0.6, '#22D3EE');
        grad.addColorStop(0.8, '#F59E0B'); grad.addColorStop(1, '#EF4444');
        ctx.fillStyle = grad; ctx.beginPath(); ctx.roundRect(scaleX, scaleY, scaleW, 18, 9); ctx.fill();

        temps.forEach(tp => {
            const x = scaleX + tp.x * scaleW;
            ctx.strokeStyle = '#1E293B'; ctx.lineWidth = 2;
            ctx.beginPath(); ctx.moveTo(x, scaleY); ctx.lineTo(x, scaleY + 18); ctx.stroke();
            ctx.beginPath(); ctx.arc(x, scaleY + 9, 4, 0, Math.PI * 2); ctx.fillStyle = 'white'; ctx.fill();

            ctx.fillStyle = '#1E293B'; ctx.font = 'bold 11px Inter,sans-serif'; ctx.textAlign = 'center';
            tp.label.split('\n').forEach((line, li) => {
                ctx.fillText(line, x, scaleY + 40 + li * 13);
            });
        });

        // Hazards
        const hazards = [
            { title: 'Frostbite', desc: 'Instant severe burns from\ncontact with cryogens', icon: '🥶', color: '#3B82F6' },
            { title: 'Asphyxiation', desc: 'Liquid He/N₂ displaces\noxygen when evaporating', icon: '💨', color: '#EF4444' },
            { title: 'Pressure', desc: 'Cryogen expansion ratio\n1:700+ upon warming', icon: '💥', color: '#F59E0B' },
        ];

        hazards.forEach((h, i) => {
            const x = 20 + i * 185, y = 140, w = 168, h2 = 90;
            ctx.fillStyle = h.color + '10'; ctx.beginPath(); ctx.roundRect(x, y, w, h2, 10); ctx.fill();
            ctx.strokeStyle = h.color; ctx.lineWidth = 1.5; ctx.beginPath(); ctx.roundRect(x, y, w, h2, 10); ctx.stroke();

            ctx.fillStyle = h.color; ctx.font = '18px Inter,sans-serif'; ctx.textAlign = 'center';
            ctx.fillText(h.icon, x + w / 2, y + 24);
            ctx.font = 'bold 12px Inter,sans-serif';
            ctx.fillText(h.title, x + w / 2, y + 42);

            ctx.fillStyle = '#475569'; ctx.font = '10px Inter,sans-serif';
            h.desc.split('\n').forEach((line, li) => {
                ctx.fillText(line, x + w / 2, y + 58 + li * 13);
            });
        });

        ctx.fillStyle = '#475569'; ctx.font = '12px Inter,sans-serif'; ctx.textAlign = 'center';
        ctx.fillText('LHC uses 120 tonnes of liquid He  -  colder than outer space (2.7 K)', W / 2, H - 28);
        ctx.fillStyle = '#64748B'; ctx.font = '11px Inter,sans-serif';
        ctx.fillText('ODH (Oxygen Deficiency Hazard) monitoring systems protect all underground areas', W / 2, H - 10);
    }, []);
    return (
        <div className="my-8 p-6 bg-gradient-to-br from-blue-50 to-cyan-50 rounded-2xl border border-slate-200">
            <canvas ref={c} width={560} height={280} className="mx-auto block rounded-xl bg-white shadow-inner" style={{ maxWidth: '100%' }} />
        </div>
    );
};

/** 10.5 – Beam Dump System (case study) */
export const BeamDumpSystem = () => {
    const c = useRef(null);
    useEffect(() => {
        const W = 560, H = 280;
        const ctx = setupCanvas(c.current, W, H);
        ctx.fillStyle = '#FAFAFA'; ctx.fillRect(0, 0, W, H);

        ctx.fillStyle = '#1E293B'; ctx.font = 'bold 15px Inter,sans-serif'; ctx.textAlign = 'center';
        ctx.fillText('LHC Beam Dump  -  Safely Stopping 362 MJ', W / 2, 24);

        const y = 110;
        // Beam pipe
        ctx.strokeStyle = '#3B82F6'; ctx.lineWidth = 3;
        ctx.beginPath(); ctx.moveTo(30, y); ctx.lineTo(120, y); ctx.stroke();
        ctx.fillStyle = '#3B82F6'; ctx.font = 'bold 11px Inter,sans-serif'; ctx.textAlign = 'center';
        ctx.fillText('Beam', 75, y - 12);

        // Kicker magnets
        ctx.fillStyle = '#EF4444'; ctx.beginPath(); ctx.roundRect(130, y - 25, 50, 50, 6); ctx.fill();
        ctx.strokeStyle = '#DC2626'; ctx.lineWidth = 2; ctx.beginPath(); ctx.roundRect(130, y - 25, 50, 50, 6); ctx.stroke();
        ctx.fillStyle = 'white'; ctx.font = 'bold 10px Inter,sans-serif';
        ctx.fillText('Kicker', 155, y - 2); ctx.fillText('magnets', 155, y + 12);

        // Extraction line
        ctx.strokeStyle = '#F59E0B'; ctx.lineWidth = 3;
        ctx.beginPath(); ctx.moveTo(180, y); ctx.lineTo(260, y - 20); ctx.lineTo(350, y - 20); ctx.stroke();
        ctx.fillStyle = '#F59E0B'; ctx.font = '10px Inter,sans-serif'; ctx.textAlign = 'center';
        ctx.fillText('Extraction line (~700 m)', 265, y - 30);

        // Dilution magnets
        ctx.fillStyle = '#8B5CF6'; ctx.beginPath(); ctx.roundRect(360, y - 45, 45, 50, 6); ctx.fill();
        ctx.strokeStyle = '#7C3AED'; ctx.lineWidth = 2; ctx.beginPath(); ctx.roundRect(360, y - 45, 45, 50, 6); ctx.stroke();
        ctx.fillStyle = 'white'; ctx.font = 'bold 9px Inter,sans-serif';
        ctx.fillText('Dilution', 382, y - 26); ctx.fillText('magnets', 382, y - 14);

        // Beam dump block
        ctx.fillStyle = '#475569'; ctx.beginPath(); ctx.roundRect(420, y - 50, 100, 90, 10); ctx.fill();
        ctx.strokeStyle = '#1E293B'; ctx.lineWidth = 2; ctx.beginPath(); ctx.roundRect(420, y - 50, 100, 90, 10); ctx.stroke();
        ctx.fillStyle = '#F1F5F9'; ctx.font = 'bold 12px Inter,sans-serif'; ctx.textAlign = 'center';
        ctx.fillText('Graphite', 470, y - 16); ctx.fillText('Beam Dump', 470, y); ctx.fillText('(7 tonnes)', 470, y + 16);

        // Spray pattern inside dump
        ctx.strokeStyle = '#F59E0B33'; ctx.lineWidth = 1;
        for (let i = 0; i < 8; i++) {
            const angle = -0.4 + (i / 8) * 0.8;
            ctx.beginPath();
            ctx.moveTo(425, y - 5);
            ctx.lineTo(510, y - 5 + Math.tan(angle) * 85);
            ctx.stroke();
        }

        // Stats
        const statY = 175;
        const stats = [
            { label: 'Beam energy', value: '362 MJ', desc: '(per beam)', color: '#EF4444' },
            { label: 'Dump time', value: '86 μs', desc: '(~40 turns)', color: '#3B82F6' },
            { label: 'Temp rise', value: '~800°C', desc: '(at core)', color: '#F59E0B' },
            { label: 'Cooling', value: 'Water + N₂', desc: '(active)', color: '#22C55E' },
        ];

        stats.forEach((s, i) => {
            const x = 25 + i * 133;
            ctx.fillStyle = s.color + '10'; ctx.beginPath(); ctx.roundRect(x, statY, 120, 55, 8); ctx.fill();
            ctx.strokeStyle = s.color; ctx.lineWidth = 1; ctx.beginPath(); ctx.roundRect(x, statY, 120, 55, 8); ctx.stroke();
            ctx.fillStyle = s.color; ctx.font = 'bold 14px Inter,sans-serif'; ctx.textAlign = 'center';
            ctx.fillText(s.value, x + 60, statY + 20);
            ctx.fillStyle = '#475569'; ctx.font = '10px Inter,sans-serif';
            ctx.fillText(s.label, x + 60, statY + 35); ctx.fillText(s.desc, x + 60, statY + 48);
        });

        ctx.fillStyle = '#64748B'; ctx.font = '11px Inter,sans-serif'; ctx.textAlign = 'center';
        ctx.fillText('Equivalent energy to a 400 km/h train  -  safely absorbed in microseconds', W / 2, H - 10);
    }, []);
    return (
        <div className="my-8 p-6 bg-gradient-to-br from-slate-50 to-red-50 rounded-2xl border border-slate-200">
            <canvas ref={c} width={560} height={280} className="mx-auto block rounded-xl bg-white shadow-inner" style={{ maxWidth: '100%' }} />
        </div>
    );
};

/** 10.6 – Safety Interlocks */
export const SafetyInterlocks = () => {
    const c = useRef(null);
    useEffect(() => {
        const W = 560, H = 280;
        const ctx = setupCanvas(c.current, W, H);
        ctx.fillStyle = '#FAFAFA'; ctx.fillRect(0, 0, W, H);

        ctx.fillStyle = '#1E293B'; ctx.font = 'bold 15px Inter,sans-serif'; ctx.textAlign = 'center';
        ctx.fillText('Safety Interlock Systems  -  Defense in Depth', W / 2, 24);

        // Layers (concentric boxes)
        const layers = [
            { label: 'Machine Protection System', desc: 'Beam Loss Monitors, Quench Detection', color: '#EF4444', w: 460, h: 200, y: 52 },
            { label: 'Access Safety System', desc: 'Door interlocks, patrol procedures', color: '#F59E0B', w: 360, h: 140, y: 82 },
            { label: 'Beam Interlock System', desc: 'Can dump beam in < 3 turns (267 μs)', color: '#3B82F6', w: 260, h: 80, y: 112 },
        ];

        layers.forEach(l => {
            const x = (W - l.w) / 2;
            ctx.fillStyle = l.color + '08';
            ctx.beginPath(); ctx.roundRect(x, l.y, l.w, l.h, 10); ctx.fill();
            ctx.strokeStyle = l.color; ctx.lineWidth = 2;
            ctx.beginPath(); ctx.roundRect(x, l.y, l.w, l.h, 10); ctx.stroke();

            ctx.fillStyle = l.color; ctx.font = 'bold 12px Inter,sans-serif'; ctx.textAlign = 'center';
            ctx.fillText(l.label, W / 2, l.y + 20);
            ctx.fillStyle = '#475569'; ctx.font = '10px Inter,sans-serif';
            ctx.fillText(l.desc, W / 2, l.y + 36);
        });

        // Center icon
        ctx.fillStyle = '#22C55E'; ctx.font = '30px Inter,sans-serif'; ctx.textAlign = 'center';
        ctx.fillText('✓', W / 2, 196);
        ctx.fillStyle = '#22C55E'; ctx.font = 'bold 11px Inter,sans-serif';
        ctx.fillText('SAFE', W / 2, 212);

        // Bottom note
        ctx.fillStyle = '#475569'; ctx.font = '12px Inter,sans-serif'; ctx.textAlign = 'center';
        ctx.fillText('Fail-safe design: any single failure triggers beam dump automatically', W / 2, H - 28);
        ctx.fillStyle = '#64748B'; ctx.font = '11px Inter,sans-serif';
        ctx.fillText('Over 10,000 interlock channels protect the LHC and its experiments', W / 2, H - 10);
    }, []);
    return (
        <div className="my-8 p-6 bg-gradient-to-br from-red-50 to-green-50 rounded-2xl border border-slate-200">
            <canvas ref={c} width={560} height={320} className="mx-auto block rounded-xl bg-white shadow-inner" style={{ maxWidth: '100%' }} />
        </div>
    );
};

/** 10.10 – Safety Chapter Summary */
export const SafetySummary = () => {
    const c = useRef(null);
    useEffect(() => {
        const W = 560, H = 260;
        const ctx = setupCanvas(c.current, W, H);
        ctx.fillStyle = '#FAFAFA'; ctx.fillRect(0, 0, W, H);

        ctx.fillStyle = '#1E293B'; ctx.font = 'bold 15px Inter,sans-serif'; ctx.textAlign = 'center';
        ctx.fillText('Safety at CERN  -  Key Areas', W / 2, 24);

        const areas = [
            { title: 'Radiation', icon: '☢', desc: 'ALARA, dosimetry,\ncontrolled zones', color: '#EF4444' },
            { title: 'Electrical', icon: '⚡', desc: 'LOTO, grounding,\nHV training', color: '#F59E0B' },
            { title: 'Cryogenic', icon: '❄', desc: 'ODH, PPE,\nventing systems', color: '#3B82F6' },
            { title: 'Mechanical', icon: '⚙', desc: 'Heavy loads, crane\nops, tunnel safety', color: '#8B5CF6' },
            { title: 'Fire', icon: '🔥', desc: 'Detection, halon,\nevacuation', color: '#EC4899' },
        ];

        areas.forEach((a, i) => {
            const x = 12 + i * 110, y = 50, w = 100, h = 125;
            ctx.fillStyle = a.color + '10'; ctx.beginPath(); ctx.roundRect(x, y, w, h, 10); ctx.fill();
            ctx.strokeStyle = a.color; ctx.lineWidth = 1.5; ctx.beginPath(); ctx.roundRect(x, y, w, h, 10); ctx.stroke();

            ctx.fillStyle = a.color; ctx.font = '22px Inter,sans-serif'; ctx.textAlign = 'center';
            ctx.fillText(a.icon, x + w / 2, y + 28);
            ctx.font = 'bold 12px Inter,sans-serif';
            ctx.fillText(a.title, x + w / 2, y + 48);

            ctx.fillStyle = '#475569'; ctx.font = '10px Inter,sans-serif';
            a.desc.split('\n').forEach((line, li) => {
                ctx.fillText(line, x + w / 2, y + 66 + li * 12);
            });
        });

        // Key principle
        ctx.fillStyle = '#F0FDF4'; ctx.beginPath(); ctx.roundRect(50, 190, 460, 35, 8); ctx.fill();
        ctx.strokeStyle = '#86EFAC'; ctx.lineWidth = 1.5; ctx.beginPath(); ctx.roundRect(50, 190, 460, 35, 8); ctx.stroke();
        ctx.fillStyle = '#16A34A'; ctx.font = 'bold 13px Inter,sans-serif'; ctx.textAlign = 'center';
        ctx.fillText('Safety First  -  no experiment is worth risking human health', W / 2, 212);

        ctx.fillStyle = '#64748B'; ctx.font = '11px Inter,sans-serif';
        ctx.fillText('All personnel complete mandatory safety training before accessing accelerator areas', W / 2, H - 10);
    }, []);
    return (
        <div className="my-8 p-6 bg-gradient-to-br from-green-50 to-slate-50 rounded-2xl border border-slate-200">
            <canvas ref={c} width={560} height={260} className="mx-auto block rounded-xl bg-white shadow-inner" style={{ maxWidth: '100%' }} />
        </div>
    );
};
